/**
 * NotificationsPage Component
 *
 * Manages Telegram notification preferences:
 * - Telegram account linking via deep link
 * - What to send: news / trends / celebs
 * - GEO (all kinds) and tag (news only) selection
 * - Enable/disable notifications toggle
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell,
  BellOff,
  Link2,
  Unlink,
  Loader2,
  Check,
  ExternalLink,
  Globe,
  Tag,
  Save,
  Newspaper,
  TrendingUp,
  Star,
  Send,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { FlagIcon } from '../../components/GeoFlag';
import { ApiServices } from '../../services/ApiServices';
import { WORLDWIDE } from '../../constants/regions';

const TAGS_ALL_LABEL = 'Все теги';

/** What the bot sends (notifications/constants.py NOTIFICATION_CONTENT_TYPES) */
const CONTENT_TYPES = [
  {
    value: 'news',
    label: 'Новости',
    icon: Newspaper,
    hint: 'Каждая новость сразу после разметки, по регионам и тегам',
  },
  {
    value: 'trends',
    label: 'Тренды',
    icon: TrendingUp,
    hint: 'Топ-10 инфоповодов за сутки по каждой стране, с тегами - каждый день в 9:30 МСК',
  },
  {
    value: 'celebs',
    label: 'Селебы',
    icon: Star,
    hint: 'Топ-10 знаменитостей за сутки по каждой стране, с тегами - каждый день в 9:30 МСК',
  },
];
const DEFAULT_CONTENT_TYPES = ['news'];

const normalizeFiltersFromServer = (arr, allCode) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return [allCode];
  }
  return arr;
};

const NotificationsPage = () => {
  const [settings, setSettings] = useState(null);
  const [options, setOptions] = useState({ geos: [], tags: [] });
  const [selectedGeos, setSelectedGeos] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(DEFAULT_CONTENT_TYPES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [deepLink, setDeepLink] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsData, optionsData] = await Promise.all([
        ApiServices.getNotificationSettings(),
        ApiServices.getNotificationOptions(),
      ]);
    setSettings(settingsData);
    setSelectedGeos(settingsData.geos || []);
    setSelectedTags(settingsData.tags || []);
    setSelectedTypes(Array.isArray(settingsData.content_types) ? settingsData.content_types : DEFAULT_CONTENT_TYPES);
    setOptions(optionsData);
    } catch (err) {
      console.error('Fetch data failed:', err);
      setError('Не удалось загрузить настройки уведомлений');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    ApiServices.trackAuditEvent('page_view', 'notifications');
  }, []);

  // Poll for linking status if a deep link is active and account is not yet linked
  useEffect(() => {
    let interval;
    if (deepLink && settings && !settings.telegram_chat_id) {
      interval = setInterval(async () => {
        try {
          const updatedSettings = await ApiServices.getNotificationSettings();
          if (updatedSettings && updatedSettings.telegram_chat_id) {
            setSettings(updatedSettings);
            // Account linked! Clear deep link and interval
            setDeepLink('');
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Polling failed:', err);
        }
      }, 3000); // Check every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [deepLink, settings]);

  const handleToggle = async () => {
    setToggling(true);
    const result = await ApiServices.toggleNotifications();
    setSettings((prev) => ({ ...prev, enabled: result.enabled }));
    setToggling(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    const updated = await ApiServices.updateNotificationSettings({
      geos: selectedGeos,
      tags: selectedTags,
      content_types: selectedTypes,
      enabled: settings.enabled,
    });
    setSettings(updated);
    setSelectedGeos(normalizeFiltersFromServer(updated.geos, WORLDWIDE.code));
    setSelectedTags(normalizeFiltersFromServer(updated.tags, WORLDWIDE.code));
    if (Array.isArray(updated.content_types)) setSelectedTypes(updated.content_types);
    setSaving(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleLink = async () => {
    setLinking(true);
    const result = await ApiServices.linkTelegram();
    setDeepLink(result.deep_link);
    setLinking(false);
  };

  const handleUnlink = async () => {
    setUnlinking(true);
    await ApiServices.unlinkTelegram();
    setSettings((prev) => ({
      ...prev,
      telegram_chat_id: null,
      telegram_username: null,
      enabled: false,
    }));
    setDeepLink('');
    setUnlinking(false);
  };

  const toggleType = (type) => {
    setSelectedTypes((prev) => (
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    ));
  };

  const selectAllGeos = () => {
    setSelectedGeos([WORLDWIDE.code]);
  };

  const toggleGeo = (geo) => {
    if (geo === WORLDWIDE.code) {
      selectAllGeos();
      return;
    }
    setSelectedGeos((prev) => {
      const withoutAll = prev.filter((g) => g !== WORLDWIDE.code);
      if (withoutAll.includes(geo)) {
        return withoutAll.filter((g) => g !== geo);
      }
      return [...withoutAll, geo];
    });
  };

  const selectAllTags = () => {
    setSelectedTags([WORLDWIDE.code]);
  };

  const toggleTag = (tag) => {
    if (tag === WORLDWIDE.code) {
      selectAllTags();
      return;
    }
    setSelectedTags((prev) => {
      const withoutAll = prev.filter((t) => t !== WORLDWIDE.code);
      if (withoutAll.includes(tag)) {
        return withoutAll.filter((t) => t !== tag);
      }
      return [...withoutAll, tag];
    });
  };

  const geoAllMode =
    selectedGeos.length === 0 ||
    selectedGeos.includes(WORLDWIDE.code);
  const tagsAllMode =
    selectedTags.length === 0 ||
    selectedTags.includes(WORLDWIDE.code);

  const tagOptions = options.tags.filter((t) => t !== WORLDWIDE.code);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isTelegramLinked = settings && settings.telegram_chat_id;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8">Уведомления</h1>

      {/* Telegram Binding Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            Telegram
          </CardTitle>
          <CardDescription>
            Привяжите Telegram, чтобы получать новости, тренды и селеб
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isTelegramLinked ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium">Telegram привязан</p>
                  {settings.telegram_username && (
                    <p className="text-sm text-muted-foreground">@{settings.telegram_username}</p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnlink}
                disabled={unlinking}
                className="text-destructive hover:text-destructive"
              >
                {unlinking ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Unlink className="h-4 w-4 mr-2" />
                )}
                Отвязать
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {deepLink ? (
                <div className="rounded-lg border p-4 bg-muted/30">
                  <p className="text-sm mb-3">
                    Нажмите на ссылку ниже, чтобы открыть бот в Telegram и привязать аккаунт:
                  </p>
                  <a
                    href={deepLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0088cc] hover:bg-[#0077b5] text-white rounded-lg font-medium transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Открыть в Telegram
                  </a>
                  <p className="text-xs text-muted-foreground mt-3">
                    Ссылка действительна 15 минут. После привязки обновите страницу.
                  </p>
                </div>
              ) : (
                <Button onClick={handleLink} disabled={linking}>
                  {linking ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Link2 className="h-4 w-4 mr-2" />
                  )}
                  Подключить Telegram
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Toggle Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {settings.enabled ? (
              <Bell className="h-5 w-5 text-green-600" />
            ) : (
              <BellOff className="h-5 w-5 text-muted-foreground" />
            )}
            Статус уведомлений
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {settings.enabled ? 'Уведомления включены' : 'Уведомления выключены'}
              </p>
              <p className="text-sm text-muted-foreground">
                {settings.enabled
                  ? 'Бот присылает выбранное ниже по вашим регионам'
                  : 'Включите, чтобы получать уведомления'}
              </p>
            </div>
            <Button
              variant={settings.enabled ? 'outline' : 'default'}
              onClick={handleToggle}
              disabled={toggling || !isTelegramLinked}
            >
              {toggling ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : settings.enabled ? (
                <BellOff className="h-4 w-4 mr-2" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              {settings.enabled ? 'Выключить' : 'Включить'}
            </Button>
          </div>
          {!isTelegramLinked && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-3">
              Привяжите Telegram, чтобы управлять уведомлениями
            </p>
          )}
        </CardContent>
      </Card>

      {/* What to send */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Что присылать
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {CONTENT_TYPES.map(({ value, label, icon: Icon, hint }) => {
            const checked = selectedTypes.includes(value);
            return (
              <label
                key={value}
                htmlFor={`notif-type-${value}`}
                className="flex items-center justify-between gap-4 rounded-lg px-3 py-3 cursor-pointer hover:bg-accent/50 transition-colors"
              >
                <span className="flex items-start gap-3 min-w-0">
                  <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${checked ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="min-w-0">
                    <span className="block font-medium">{label}</span>
                    <span className="block text-sm text-muted-foreground">{hint}</span>
                  </span>
                </span>
                <Switch
                  id={`notif-type-${value}`}
                  checked={checked}
                  onCheckedChange={() => toggleType(value)}
                />
              </label>
            );
          })}
          {selectedTypes.length === 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 px-3 pt-2">
              Ничего не выбрано - бот не будет ничего присылать
            </p>
          )}
        </CardContent>
      </Card>

      {/* GEO Selection Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Регионы (GEO)
          </CardTitle>
          <CardDescription>
            Для новостей, трендов и селеб. «{WORLDWIDE.name}» - все регионы, иначе выберите страны
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={geoAllMode ? 'default' : 'outline'}
              className={`cursor-pointer transition-all hover:scale-105 ${
                geoAllMode
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-accent'
              }`}
              onClick={() => toggleGeo(WORLDWIDE.code)}
            >
              {WORLDWIDE.name}
            </Badge>
            {options.geos.length === 0 ? (
              <span className="text-sm text-muted-foreground self-center">
                Нет дополнительных кодов в базе
              </span>
            ) : (
              options.geos.map((geo) => {
                const isSelected = !geoAllMode && selectedGeos.includes(geo);
                return (
                  <Badge
                    key={geo}
                    variant={isSelected ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all hover:scale-105 gap-1.5 ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    }`}
                    onClick={() => toggleGeo(geo)}
                  >
                    <FlagIcon geo={geo} />
                    {geo}
                  </Badge>
                );
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {geoAllMode
              ? `Регионы: ${WORLDWIDE.name}`
              : `Выбрано регионов: ${selectedGeos.length}`}
          </p>
        </CardContent>
      </Card>

      {/* Tags Selection Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Теги
          </CardTitle>
          <CardDescription>
            Только для новостей: по каким тегам присылать
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={tagsAllMode ? 'default' : 'outline'}
              className={`cursor-pointer transition-all hover:scale-105 ${
                tagsAllMode
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-accent'
              }`}
              onClick={() => toggleTag(WORLDWIDE.code)}
            >
              {TAGS_ALL_LABEL}
            </Badge>
            {tagOptions.length === 0 ? (
              <span className="text-sm text-muted-foreground self-center">
                Нет тегов в базе
              </span>
            ) : (
              tagOptions.map((tag) => {
                const isSelected = !tagsAllMode && selectedTags.includes(tag);
                return (
                  <Badge
                    key={tag}
                    variant={isSelected ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all hover:scale-105 ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    }`}
                    onClick={() => toggleTag(tag)}
                  >
                    #{tag}
                  </Badge>
                );
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {tagsAllMode
              ? `Теги: ${TAGS_ALL_LABEL}`
              : `Выбрано тегов: ${selectedTags.length}`}
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="min-w-[160px]"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : saveSuccess ? (
            <Check className="h-4 w-4 mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saveSuccess ? 'Сохранено!' : 'Сохранить настройки'}
        </Button>
      </div>
    </div>
  );
};

export default NotificationsPage;
